/* eslint-disable react/no-unknown-property */
import { Suspense, useMemo, useState, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { Shape, BufferGeometry, Float32BufferAttribute, DoubleSide } from "three";
import { resolveDoorStandard } from "../lib/configurator/doorRules";

function InteractiveDrawer({ children, cx = 0, cy = 0, closedZ, openZ }) {
  const [open, setOpen] = useState(false);
  const groupRef = useRef();

  useFrame((state, delta) => {
    if (!groupRef.current) return;
    const targetZ = open ? openZ : closedZ;
    groupRef.current.position.z += (targetZ - groupRef.current.position.z) * 10 * delta;
  });

  return (
    <group
      ref={groupRef}
      position={[cx, cy, closedZ]}
      onClick={(e) => {
        e.stopPropagation();
        setOpen(!open);
      }}
      onPointerOver={(e) => {
        e.stopPropagation();
        document.body.style.cursor = 'pointer';
      }}
      onPointerOut={(e) => {
        document.body.style.cursor = 'auto';
      }}
    >
      {children}
    </group>
  );
}

function ProfilePanel({ points, z, depth = 0.01, color, materialProps = {} }) {
  const shape = useMemo(() => {
    const next = new Shape();
    points.forEach(([x, y], index) => {
      if (index === 0) next.moveTo(x, y);
      else next.lineTo(x, y);
    });
    next.closePath();
    return next;
  }, [points]);

  return (
    <mesh position={[0, 0, z]}>
      <extrudeGeometry args={[shape, { depth, bevelEnabled: false }]} />
      <meshStandardMaterial color={color} {...materialProps} />
    </mesh>
  );
}

/** Cintre 3D (crochet + corps triangulaire) accroché sur la barre de penderie — évite la
 * confusion visuelle avec une poignée de porte (signalée par l'utilisateur). Tailles en
 * unités de scène déjà à l'échelle (1 unité = 1m, cf. `scale = 0.01` pour cm -> unités). */
function Hanger3D({ x, y, z }) {
  return (
    <group position={[x, y, z]}>
      {/* Crochet : anneau partiel accroché juste sous la barre */}
      <mesh position={[0, 0.006, 0]}>
        <torusGeometry args={[0.009, 0.0012, 6, 14, Math.PI * 1.5]} />
        <meshStandardMaterial color="#6B6F73" metalness={0.6} roughness={0.4} />
      </mesh>
      {/* Corps triangulaire (épaules du cintre) */}
      <ProfilePanel
        points={[[0, 0.006], [0.05, -0.04], [-0.05, -0.04]]}
        z={-0.0015}
        depth={0.003}
        color="#C9A876"
        materialProps={{ roughness: 0.6 }}
      />
    </group>
  );
}


/**
 * Surface de toit paramétrique : grille de points (x, z) -> hauteur, pour représenter
 * une pente latérale, une pente arrière, ou la combinaison des deux (creux d'angle).
 */
function SlopedRoofPanel({ w_actual, scaledDepth, roofYFn, color, segX = 16, segZ = 10 }) {
  const geometry = useMemo(() => {
    const geo = new BufferGeometry();
    const positions = [];
    for (let iz = 0; iz <= segZ; iz++) {
      const z = -scaledDepth / 2 + (scaledDepth * iz) / segZ;
      for (let ix = 0; ix <= segX; ix++) {
        const x = -w_actual / 2 + (w_actual * ix) / segX;
        positions.push(x, roofYFn(x, z), z);
      }
    }
    const indices = [];
    const rowLen = segX + 1;
    for (let iz = 0; iz < segZ; iz++) {
      for (let ix = 0; ix < segX; ix++) {
        const a = iz * rowLen + ix;
        const b = a + 1;
        const c = a + rowLen;
        const e = c + 1;
        indices.push(a, c, b, b, c, e);
      }
    }
    geo.setAttribute("position", new Float32BufferAttribute(positions, 3));
    geo.setIndex(indices);
    geo.computeVertexNormals();
    return geo;
  }, [w_actual, scaledDepth, roofYFn, segX, segZ]);
  return (
    <mesh geometry={geometry}>
      <meshStandardMaterial color={color} side={DoubleSide} />
    </mesh>
  );
}

/** Panneau latéral (gauche/droite) dont le haut suit la pente arrière le long de la profondeur. */
function SlopedSidePanel({ x, scaledDepth, bottomY, topAtZ, color, segments = 10 }) {
  const geometry = useMemo(() => {
    const geo = new BufferGeometry();
    const positions = [];
    for (let i = 0; i <= segments; i++) {
      const z = -scaledDepth / 2 + (scaledDepth * i) / segments;
      positions.push(x, bottomY, z);
      positions.push(x, topAtZ(z), z);
    }
    const indices = [];
    for (let i = 0; i < segments; i++) {
      const a = i * 2;
      const b = a + 1;
      const c = a + 2;
      const e = a + 3;
      indices.push(a, c, b, b, c, e);
    }
    geo.setAttribute("position", new Float32BufferAttribute(positions, 3));
    geo.setIndex(indices);
    geo.computeVertexNormals();
    return geo;
  }, [x, scaledDepth, bottomY, topAtZ, segments]);
  return (
    <mesh geometry={geometry}>
      <meshStandardMaterial color={color} side={DoubleSide} />
    </mesh>
  );
}

/**
 * Poignée 3D abstraite — mêmes 3 références visibles que le catalogue 2D (push-to-open/tip-on
 * reste invisible). Posée EN SURPLUS du panneau de porte/tiroir existant (jamais une
 * modification de sa géométrie), juste décalée vers l'avant.
 */
const HANDLE_3D_COLOR = { profil_alu: "#C7CBCE", recto_verso: "#AEB3B6", stripe: "#9A9D9F", bequille: "#B0B4B8" };
function Handle3D({ svgType, centerX, centerY, z, availW }) {
  if (!svgType || svgType === "push") return null;
  const color = HANDLE_3D_COLOR[svgType] || "#C7CBCE";
  const matProps = { color, roughness: 0.3, metalness: 0.75 };
  const barW = Math.max(0.06, Math.min(availW * 0.7, 0.35));
  const barH = 0.012;
  const barD = 0.012;
  if (svgType === "bequille") {
    /* Béquille classique : rosette cylindrique + levier horizontal + embout arrondi.
       Taille majorée pour être visible sur la porte (pas de carte env → dark color + emissive). */
    const handleMat = { color: "#4A4E52", roughness: 0.5, metalness: 0.0, emissive: "#888888", emissiveIntensity: 0.35 };
    const rosR = 0.018;
    const rosDepth = 0.005;
    const leverLen = Math.min(barW * 0.42, 0.14);
    const leverR = 0.006;
    return (
      <group position={[centerX, centerY, z]}>
        {/* Rosette (plaque circulaire) */}
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[rosR, rosR, rosDepth, 20]} />
          <meshStandardMaterial {...handleMat} />
        </mesh>
        {/* Levier horizontal */}
        <mesh position={[-leverLen / 2, -0.002, rosDepth / 2 + leverR]} rotation={[0, 0, 0.04]}>
          <boxGeometry args={[leverLen, leverR * 2, leverR * 2]} />
          <meshStandardMaterial {...handleMat} />
        </mesh>
        {/* Embout arrondi */}
        <mesh position={[-leverLen, -0.004, rosDepth / 2 + leverR]}>
          <sphereGeometry args={[leverR * 1.5, 10, 10]} />
          <meshStandardMaterial {...handleMat} />
        </mesh>
      </group>
    );
  }
  if (svgType === "recto_verso") {
    const footW = 0.012;
    return (
      <group position={[centerX, centerY, z]}>
        <mesh position={[-barW / 2 + footW / 2, 0, barD / 2]}>
          <boxGeometry args={[footW, barH * 1.6, barD]} />
          <meshStandardMaterial {...matProps} />
        </mesh>
        <mesh position={[barW / 2 - footW / 2, 0, barD / 2]}>
          <boxGeometry args={[footW, barH * 1.6, barD]} />
          <meshStandardMaterial {...matProps} />
        </mesh>
        <mesh position={[0, 0, barD]}>
          <boxGeometry args={[barW, barH, barD]} />
          <meshStandardMaterial {...matProps} />
        </mesh>
      </group>
    );
  }
  const thickness = svgType === "stripe" ? 0.009 : barD;
  return (
    <mesh position={[centerX, centerY, z]}>
      <boxGeometry args={[barW, barH, thickness]} />
      <meshStandardMaterial {...matProps} />
    </mesh>
  );
}

/**
 * Vue 3D interactive du meuble.
 * L'utilisateur peut faire tourner le meuble à 360° à la souris (OrbitControls).
 * Rendu simple : caisson boxé + séparations verticales + tablettes/tiroirs par section.
 */
function CabinetMesh({ totalWidth, height, depth, sections, sectionWidths, carcassColor, doorsColor, doorsDifferent, showDoors, templateVariant, options, sdbDrawers = 0, doorsFinishId, handleSvgType = null, printMode = false }) {
  const isMirrorGlobal = doorsFinishId === "miroir";
  const doorFill = doorsDifferent && doorsColor ? doorsColor : carcassColor;
  // Sans carte d'environnement dans la scène, toute metalness>0 assombrit fortement la
  // couleur diffuse (rien à refléter) : metalness très faible + couleur quasi blanche
  // pour un effet "verre clair" lisible, plutôt qu'un gris moyen/noir.
  const mirrorMatProps = { color: "#F2F5F5", metalness: 0.08, roughness: 0.05, transparent: true, opacity: 0.95 };
  const doorMatProps = isMirrorGlobal ? mirrorMatProps : { color: doorFill, transparent: true, opacity: 0.42, roughness: 0.55, metalness: 0.05 };
  const drawerMatProps = isMirrorGlobal ? mirrorMatProps : { color: doorFill };
  const sdbDrawerMatProps = isMirrorGlobal ? mirrorMatProps : { color: doorFill, roughness: 0.55, metalness: 0.05 };
  // Cotes en cm, on convertit en unités three.js (1 unité = 100 cm pour ne pas être trop grand)
  const scale = 0.01;
  const w = totalWidth * scale;
  const h = height * scale;
  const d = depth * scale;
  const isSdbVariant = (templateVariant === "sdb" || templateVariant === "sdb_column");
  // Règle impérative : la Salle de Bain n'a JAMAIS de plinthe (toujours pieds elliptiques
  // apparents, ou pieds génériques), quelle que soit la valeur stockée dans options.plinth
  // (anciens devis/brouillons sauvegardés avant ce correctif inclus).
  const hasPlinth = isSdbVariant ? false : options?.plinth !== false;
  const isSuspended = !isSdbVariant && options?.plinth === false && options?.feet_visible === false;
  
  const rawSdbFootH = isSdbVariant ? ((options?.sdb_foot_ellipse_height || 0) * 0.001) : 0;
  // Si on est en mode "Posé au sol / Suspendu" (isSuspended), on force les pieds à 0
  const sdbFootH = isSuspended ? 0 : rawSdbFootH;
  
  const footH = sdbFootH > 0 ? sdbFootH : 0.10;   // pieds à hauteur réelle
  const plinthH = footH; // La plinthe s'adapte à la hauteur des pieds choisis
  const feetVisible = !hasPlinth && !isSuspended;

  // Somme réelle des largeurs (asymétriques) pour placement précis
  const totalReal = sectionWidths.reduce((a, b) => a + b, 0) || totalWidth;
  const w_actual = totalReal * scale;
  // Sous-pente : la géométrie 3D reproduit la découpe en biais.
  // Le rendu ne modifie JAMAIS le state des aménagements (pas de suppression d'éléments).
  const underSlope = options?.under_slope_enabled === true;
  const slopeDirection = options?.under_slope_direction === "right" ? "right" : "left";
  const totalHeightMm = height * 10;
  const lowHeightMm = Math.max(100, Math.min(totalHeightMm - 10, options?.under_slope_low_height_mm || Math.round(totalHeightMm * 0.6)));
  const bevelWidthMm = Math.max(100, Math.min(totalReal * 10, options?.under_slope_bevel_width_mm || Math.round(Math.min(totalReal * 10, 600))));
  const lowY = (lowHeightMm / 10) * scale - h / 2;
  const bevelW = (bevelWidthMm / 10) * scale;
  const profilePointsRaw = underSlope
    ? slopeDirection === "left"
      ? [[-w_actual / 2, -h / 2], [w_actual / 2, -h / 2], [w_actual / 2, h / 2], [-w_actual / 2 + bevelW, h / 2], [-w_actual / 2, lowY]]
      : [[-w_actual / 2, -h / 2], [w_actual / 2, -h / 2], [w_actual / 2, lowY], [w_actual / 2 - bevelW, h / 2], [-w_actual / 2, h / 2]]
    : [[-w_actual / 2, -h / 2], [w_actual / 2, -h / 2], [w_actual / 2, h / 2], [-w_actual / 2, h / 2]];
  const roofYAt = (x) => {
    if (!underSlope) return h / 2;
    if (slopeDirection === "left") {
      if (x >= -w_actual / 2 + bevelW) return h / 2;
      return lowY + ((x + w_actual / 2) / bevelW) * (h / 2 - lowY);
    }
    if (x <= w_actual / 2 - bevelW) return h / 2;
    return h / 2 - ((x - (w_actual / 2 - bevelW)) / bevelW) * (h / 2 - lowY);
  };
  const usableSpanAt = (sectionLeft, sectionWidth, yMm) => {
    let start = sectionLeft;
    let end = sectionLeft + sectionWidth;
    if (!underSlope || yMm <= lowHeightMm) return { start, end };
    const riseRatio = Math.min(1, Math.max(0, (yMm - lowHeightMm) / (totalHeightMm - lowHeightMm)));
    if (slopeDirection === "left") start = Math.max(start, -w_actual / 2 + bevelW * riseRatio);
    else end = Math.min(end, w_actual / 2 - bevelW * riseRatio);
    return { start, end };
  };

  // Sous-pente arrière : le plafond descend en allant vers le fond (z négatif), sur une
  // profondeur de "retrait arrière" (bevel), symétrique à la logique de la pente latérale.
  const underSlopeRear = options?.under_slope_rear_enabled === true;
  const rearLowHeightMm = Math.max(100, Math.min(totalHeightMm - 10, options?.under_slope_rear_low_height_mm || 1200));
  const rearBevelDepthMm = Math.max(100, Math.min(depth * 10, options?.under_slope_rear_bevel_depth_mm || 450));
  const rearLowY = (rearLowHeightMm / 10) * scale - h / 2;
  const rearBevelD = (rearBevelDepthMm / 10) * scale;
  const rearMaxDrop = h / 2 - rearLowY;
  const lateralDropAt = (x) => (underSlope ? h / 2 - roofYAt(x) : 0);
  const dropRearAt = (z) => {
    if (!underSlopeRear) return 0;
    const backZ = -d / 2;
    if (z >= backZ + rearBevelD) return 0;
    const t = Math.max(0, Math.min(1, (z - backZ) / rearBevelD));
    return rearMaxDrop * (1 - t);
  };
  // Hauteur de toit combinée (latérale + arrière) : on retient la pente la plus contraignante
  // à chaque point, ce qui reproduit correctement un angle de toit à deux pentes si les deux sont actives.
  const combinedRoofYAt = (x, z) => h / 2 - Math.max(lateralDropAt(x), dropRearAt(z));
  // Hauteur plafond imposée au fond du meuble par la pente arrière (sert à plafonner le fond
  // et les montants, qui sont construits à partir du profil latéral uniquement).
  const rearCapY = underSlopeRear ? rearLowY : Infinity;
  // Le fond est au niveau le plus bas de la pente arrière : on plafonne son profil latéral
  // (sinon il reste à hauteur pleine alors que le toit arrière y est déjà descendu).
  const profilePoints = profilePointsRaw.map(([x, y]) => [x, Math.min(y, rearCapY)]);

  // Positions de séparation en X (offsets cumulés depuis gauche)
  const sepX = useMemo(() => {
    const out = [];
    let acc = 0;
    for (let i = 0; i < sections.length - 1; i++) {
      acc += sectionWidths[i] * scale;
      out.push(acc - w_actual / 2);
    }
    return out;
  }, [sections, sectionWidths, w_actual]);

  const isSdbNoTop = (templateVariant === "sdb" || templateVariant === "sdb_column") && (!options?.sdb_top_finish || options?.sdb_top_finish === "sans_vasque");

  // FIX: Set Y to strictly baseOffsetY
  return (
    <group position={[0, h / 2 + (hasPlinth ? plinthH : ((templateVariant === "sdb" || templateVariant === "sdb_column") && sdbFootH > 0 ? sdbFootH : (feetVisible ? footH : 0))), 0]}>
      {/* Fond découpé suivant la pente — masqué si "Sans fond" */}
      {options?.has_back_panel !== false && (
        <ProfilePanel points={profilePoints} z={-d / 2 + 0.005} color={carcassColor} />
      )}
      {/* Côté gauche — suit la pente arrière le long de la profondeur si activée */}
      <SlopedSidePanel
        x={-w_actual / 2 + 0.005}
        scaledDepth={d}
        bottomY={-h / 2 + 0.01}
        topAtZ={(z) => combinedRoofYAt(-w_actual / 2 + 0.005, z) - 0.01}
        color={carcassColor}
      />
      {/* Côté droit */}
      <SlopedSidePanel
        x={w_actual / 2 - 0.005}
        scaledDepth={d}
        bottomY={-h / 2 + 0.01}
        topAtZ={(z) => combinedRoofYAt(w_actual / 2 - 0.005, z) - 0.01}
        color={carcassColor}
      />
      {/* Dessus : surface paramétrique combinant pente latérale et pente arrière.
          Salle de Bain "Sans vasque" = rien sur le dessus (le tiroir du haut reste ouvert
          vers le haut, on voit son intérieur blanc). */}
      {isSdbNoTop ? null : (underSlope || underSlopeRear) ? (
        <SlopedRoofPanel w_actual={w_actual - 0.01} scaledDepth={d - 0.005} roofYFn={combinedRoofYAt} color={carcassColor} />
      ) : (
        <mesh position={[0, h / 2 - 0.005, 0]}>
          <boxGeometry args={[w_actual - 0.01, 0.01, d - 0.005]} />
          <meshStandardMaterial color={carcassColor} />
        </mesh>
      )}
      {/* Dessous */}
      <mesh position={[0, -h / 2 + 0.005, 0]}>
        <boxGeometry args={[w_actual - 0.01, 0.01, d - 0.005]} />
        <meshStandardMaterial color={carcassColor} />
      </mesh>
      {/* Séparations verticales */}
      {sepX.map((x, i) => {
        const sepTop = Math.min(roofYAt(x), rearCapY);
        const bottomY = -h / 2 + 0.01;
        const blockedRanges = (sections.flatMap((ownerSection, ownerIdx) => (ownerSection.elements || [])
          .filter((element) => element.kind === "drawer" && (element.span_sections || 1) > 1 && ownerIdx <= i && ownerIdx + (element.span_sections || 1) > i + 1))
          .map((element) => {
            const center = (element.y_mm / 10) * scale - h / 2;
            const halfH = Math.max(0.015, ((element.height_mm || 150) / 10) * scale / 2) + 0.002;
            return { start: Math.max(bottomY, center - halfH), end: Math.min(sepTop, center + halfH) };
          })
          .filter((range) => range.end > range.start)
          .sort((a, b) => a.start - b.start));
        const dividerSegments = [];
        let cursorY = bottomY;
        blockedRanges.forEach((range) => {
          if (range.start > cursorY) dividerSegments.push({ start: cursorY, end: range.start });
          cursorY = Math.max(cursorY, range.end);
        });
        if (cursorY < sepTop - 0.01) dividerSegments.push({ start: cursorY, end: sepTop - 0.01 });
        // Sans tiroir traversant, le montant doit suivre le toit sur toute la profondeur.
        // Un simple boxGeometry plafonné au fond créait un arrêt visible sous la pente à l'avant.
        if (blockedRanges.length === 0) {
          return <SlopedSidePanel key={`sep-${i}`} x={x} scaledDepth={d} bottomY={bottomY} topAtZ={(z) => combinedRoofYAt(x, z) - 0.01} color={carcassColor} />;
        }
        return (
          <group key={`sep-${i}`}>
            {dividerSegments.map((segment, segmentIdx) => (
              <mesh key={`sep-${i}-${segmentIdx}`} position={[x, (segment.start + segment.end) / 2, 0]}>
                <boxGeometry args={[0.008, segment.end - segment.start, d]} />
                <meshStandardMaterial color={carcassColor} />
              </mesh>
            ))}
          </group>
        );
      })}

      {/* Fileurs latéraux/haut (habillage) — descendent jusqu'au sol */}
      {options?.fileur_left && (() => {
        const fw = ((options.fileur_left_width || 1.5)) * scale;
        const footExtra = hasPlinth ? plinthH : (feetVisible ? footH : 0);
        const fileurH = h + footExtra;
        return (
          <mesh position={[-w_actual / 2 - fw / 2, -footExtra / 2, 0]}>
            <boxGeometry args={[fw, fileurH, d]} />
            <meshStandardMaterial color={carcassColor} />
          </mesh>
        );
      })()}
      {options?.fileur_right && (() => {
        const fw = ((options.fileur_right_width || 1.5)) * scale;
        const footExtra = hasPlinth ? plinthH : (feetVisible ? footH : 0);
        const fileurH = h + footExtra;
        return (
          <mesh position={[w_actual / 2 + fw / 2, -footExtra / 2, 0]}>
            <boxGeometry args={[fw, fileurH, d]} />
            <meshStandardMaterial color={carcassColor} />
          </mesh>
        );
      })()}
      {options?.fileur_top && (() => {
        const fileurTopThickness = ((options.fileur_top_width || 1.5)) * scale;
        const caissonHeight = h;
        return (
          <mesh position={[0, (caissonHeight / 2) + (fileurTopThickness / 2), 0]}>
            <boxGeometry args={[w_actual, fileurTopThickness, d]} />
            <meshStandardMaterial color={carcassColor} />
          </mesh>
        );
      })()}

      {/* Portes (variant "coulissant" → pas de portes 3D, non fournies) */}
      {showDoors && templateVariant !== "coulissant" && sections.map((sec, idx) => {
        if (!sec.has_door) return null;
        const sectionDoorMat = (isMirrorGlobal || sec.door_mirror) ? mirrorMatProps : doorMatProps;
        const sw = sectionWidths[idx] * scale;
        let cx = -w_actual / 2;
        for (let i = 0; i < idx; i++) cx += sectionWidths[i] * scale;
        cx += sw / 2;
        const sectionLeft = cx - sw / 2;
        const sectionRight = cx + sw / 2;
        
        if (templateVariant === "sdb_column") {
          // 2 portes superposées
          const doorH = (h - 0.006) / 2;
          const columnHandleZ = (d / 2 - 0.005) + 0.004 + 0.0035;
          return (
            <group key={`door-${idx}`}>
              {/* Porte Basse */}
              <mesh position={[cx, -doorH/2 - 0.001, d / 2 - 0.005]}>
                <boxGeometry args={[sw - 0.004, doorH, 0.008]} />
                <meshStandardMaterial {...sectionDoorMat} />
              </mesh>
              <Handle3D svgType={handleSvgType === "push" ? "push" : "bequille"} centerX={cx} centerY={-doorH / 2 - 0.001 + doorH * 0.08} z={columnHandleZ} availW={sw - 0.004} />
              {/* Porte Haute */}
              <mesh position={[cx, doorH/2 + 0.001, d / 2 - 0.005]}>
                <boxGeometry args={[sw - 0.004, doorH, 0.008]} />
                <meshStandardMaterial {...sectionDoorMat} />
              </mesh>
              <Handle3D svgType={handleSvgType === "push" ? "push" : "bequille"} centerX={cx} centerY={doorH / 2 + 0.001 - doorH * 0.08} z={columnHandleZ} availW={sw - 0.004} />
            </group>
          );
        }

        const drawerBands = sections.flatMap((owner, ownerIdx) => (owner.elements || [])
          .filter((element) => element.kind === "drawer" && ownerIdx <= idx && idx < ownerIdx + (element.span_sections || 1))
          .map((element) => {
            const center = (element.y_mm / 10) * scale - h / 2;
            const half = ((element.height_mm || 150) / 10) * scale / 2;
            return { low: center - half, high: center + half };
          }))
          .sort((a, b) => a.low - b.low);
        let cursor = -h / 2 + 0.002;
        const rawFacadeSegments = [];
        drawerBands.forEach((band) => { if (band.low - cursor > 0.01) rawFacadeSegments.push({ low: cursor, high: band.low - 0.004 }); cursor = Math.max(cursor, band.high + 0.004); });
        if (h / 2 - 0.002 - cursor > 0.01) rawFacadeSegments.push({ low: cursor, high: h / 2 - 0.002 });
        const facadeSegments = rawFacadeSegments.map((segment, segmentIdx, list) => ({ ...segment, key: list.length === 1 ? "full" : segmentIdx === 0 ? "bottom" : segmentIdx === list.length - 1 ? "top" : `middle-${segmentIdx}` }));
        const visibleFacadeSegments = facadeSegments
          .filter((segment) => segment.high - segment.low > 0.01 && sec.door_parts?.[segment.key]?.enabled !== false)
          .map((segment) => {
            const part = sec.door_parts?.[segment.key];
            if (!part?.height_mm) return segment;
            const wanted = Math.min(segment.high - segment.low, (part.height_mm / 10) * scale);
            return segment.key === "bottom" ? { ...segment, high: segment.low + wanted } : { ...segment, low: segment.high - wanted };
          });
        if (visibleFacadeSegments.length === 0) return null;

        const kinkX = slopeDirection === "left" ? -w_actual / 2 + bevelW : w_actual / 2 - bevelW;
        const leafPoints = (left, right, low, high) => {
          const xs = [left, right];
          if (underSlope && kinkX > left && kinkX < right) xs.splice(1, 0, kinkX);
          const topAt = (x) => Math.min(high, roofYAt(x) - 0.002);
          if (Math.max(...xs.map(topAt)) - low < 0.01) return null;
          return [[left, low], [right, low], ...xs.slice().reverse().map((x) => [x, topAt(x)])];
        };
        const leavesFor = (segment) => {
          const part = sec.door_parts?.[segment.key];
          const standard = resolveDoorStandard({ ...sec, door_standard: segment.key === "full" ? sec.door_standard : (part?.standard || sec.door_standard) }, sectionWidths[idx]);
          const inner = 0.002, gap = 0.0015;
          if (standard !== "double") return [[sectionLeft + inner, sectionRight - inner]];
          return [[sectionLeft + inner, cx - gap], [cx + gap, sectionRight - inner]];
        };
        return (
          <group key={`door-${idx}`}>
            {visibleFacadeSegments.flatMap((segment) => leavesFor(segment).map(([left, right], leafIdx) => {
              const points = leafPoints(left, right, segment.low, segment.high);
              if (!points) return null;
              const part = sec.door_parts?.[segment.key];
              const leafStandard = resolveDoorStandard({ ...sec, door_standard: segment.key === "full" ? sec.door_standard : (part?.standard || sec.door_standard) }, sectionWidths[idx]);
              const masterIdx = sec.door_double_master === "right" ? 1 : 0;
              const isMasterLeaf = leafStandard !== "double" || leafIdx === masterIdx;
              const handleZ = (d / 2 - 0.009) + 0.008 + 0.0035;
              return (
                <group key={`${segment.key}-${leafIdx}`}>
                  <ProfilePanel points={points} z={d / 2 - 0.009} depth={0.008} color={sectionDoorMat.color} materialProps={sectionDoorMat} />
                  {isMasterLeaf && <Handle3D svgType={handleSvgType === "push" ? "push" : "bequille"} centerX={(left + right) / 2} centerY={segment.low + (segment.high - segment.low) * 0.58} z={handleZ} availW={right - left} />}
                </group>
              );
            }))}
          </group>
        );
      })}

      {/* Éléments intérieurs : tablettes, penderies, tiroirs */}
      {sections.map((sec, idx) => {
        const sw = sectionWidths[idx] * scale;
        let cx = -w_actual / 2;
        for (let i = 0; i < idx; i++) cx += sectionWidths[i] * scale;
        cx += sw / 2;
        const sectionLeft = cx - sw / 2;
        const els = sec.elements || [];
        return (
          <group key={`interior-${idx}`}>
            {els.map((el) => {
              const yEl = (el.y_mm / 10) * scale - h / 2;
              const elementSpanWidth = el.kind === "drawer" && (el.span_sections || 1) > 1 && idx + (el.span_sections || 1) <= sections.length
                ? sectionWidths.slice(idx, idx + (el.span_sections || 1)).reduce((sum, sectionWidth) => sum + sectionWidth * scale, 0)
                : sw;
              const spanAtElement = usableSpanAt(sectionLeft, elementSpanWidth, el.kind === "drawer" ? el.y_mm + (el.height_mm || 150) / 2 : el.y_mm);
              const usableW = spanAtElement.end - spanAtElement.start;
              const elementCx = (spanAtElement.start + spanAtElement.end) / 2;
              if (usableW <= 0.015) return null;

              // Phase 4 — Clipping vertical sous pente : hauteur plafond à la position de l'élément
              const roofAtEl = (underSlope || underSlopeRear) ? combinedRoofYAt(elementCx, 0) - 0.008 : h / 2;

              if (el.kind === "shelf_fixed" || el.kind === "shelf_mobile") {
                if (yEl > roofAtEl) return null;
                return (
                  <mesh key={el.id} position={[elementCx, yEl, 0]}>
                    <boxGeometry args={[usableW - 0.008, 0.006, d - 0.02]} />
                    <meshStandardMaterial color={carcassColor} />
                  </mesh>
                );
              }
              if (el.kind === "penderie") {
                if (yEl > roofAtEl) return null;
                const railSpan = usableW - 0.02;
                const hangerCount = railSpan > 0.3 ? 3 : railSpan > 0.15 ? 2 : 1;
                const hangerOffsets = Array.from({ length: hangerCount }, (_, k) => (railSpan * (k + 1)) / (hangerCount + 1) - railSpan / 2);
                return (
                  <group key={el.id}>
                    <mesh position={[elementCx, yEl, 0]} rotation={[0, 0, Math.PI / 2]}>
                      <cylinderGeometry args={[0.008, 0.008, railSpan, 12]} />
                      <meshStandardMaterial color="#8E9297" metalness={0.85} roughness={0.25} />
                    </mesh>
                    {hangerOffsets.map((dx, k) => (
                      <Hanger3D key={`${el.id}-hanger-${k}`} x={elementCx + dx} y={yEl} z={0} />
                    ))}
                  </group>
                );
              }
              if (el.kind === "mirror") {
                let mH = Math.max(0.02, ((el.height_mm || 0) / 10) * scale);
                const mW = Math.min(usableW - 0.01, Math.max(0.02, ((el.width_mm || 0) / 10) * scale));
                // Clip mirror top to roof
                if (yEl + mH / 2 > roofAtEl) mH = Math.max(0.01, (roofAtEl - yEl) * 2);
                if (mH <= 0.01) return null;
                return (
                  <group key={el.id} position={[elementCx, yEl, -d / 2 + 0.006]}>
                    {/* Cadre clair : rend le miroir immédiatement identifiable dans le volume sombre du caisson */}
                    <mesh position={[0, 0, -0.001]}>
                      <boxGeometry args={[mW + 0.012, mH + 0.012, 0.006]} />
                      <meshStandardMaterial color="#E8DFC8" roughness={0.5} />
                    </mesh>
                    <mesh position={[0, 0, 0.002]}>
                      <boxGeometry args={[mW, mH, 0.004]} />
                      <meshStandardMaterial color="#CFE4EC" metalness={0.5} roughness={0.15} envMapIntensity={1.4} />
                    </mesh>
                  </group>
                );
              }
              if (el.kind === "drawer") {
                // Tiroir réaliste (quincaillerie Merivobox) :
                // - Seuil ≤200mm (Merivo M) : tiroir standard, parois pleine hauteur
                // - Seuil >200mm (Merivo E / casserolier) : côtés bas fixes (83mm),
                //   façade pleine hauteur, tubes de rehausse latéraux
                const TUBE_THRESHOLD_MM = 200;
                const STANDARD_SIDE_MM = 83;
                let heightMm = el.height_mm || 150;
                let drawerH = Math.max(0.03, (heightMm / 10) * scale);
                // Clip drawer top to roof
                if (yEl + drawerH / 2 > roofAtEl) drawerH = Math.max(0.02, (roofAtEl - yEl) * 2);
                if (drawerH <= 0.02) return null;
                heightMm = (drawerH / scale) * 10;
                const drawerDepth = d - 0.03;
                const faceThick = 0.015;
                const closedZ = d / 2 - 0.02;
                const openZ = closedZ + drawerDepth * 0.8;
                const isTall = heightMm > TUBE_THRESHOLD_MM;
                const sideH = isTall ? Math.max(0.02, (STANDARD_SIDE_MM / 10) * scale) : drawerH;
                const sideOffset = isTall ? -drawerH / 2 + sideH / 2 : 0;
                const tubeMat = { color: "#A0A4A8", roughness: 0.35, metalness: 0.2 };
                return (
                  <InteractiveDrawer key={el.id} cx={elementCx} cy={yEl} closedZ={closedZ} openZ={openZ}>
                    {/* Façade (toujours pleine hauteur) */}
                    <mesh position={[0, 0, 0]}>
                      <boxGeometry args={[usableW - 0.008, drawerH, faceThick]} />
                      <meshStandardMaterial {...drawerMatProps} />
                    </mesh>
                    {/* Poignée intégrée */}
                    <mesh position={[0, drawerH / 2 - 0.01, faceThick / 2 + 0.001]}>
                      <boxGeometry args={[usableW * 0.4, 0.012, 0.002]} />
                      <meshStandardMaterial color="#333" metalness={0.6} roughness={0.4} />
                    </mesh>
                    {/* Fond du tiroir */}
                    <mesh position={[0, -drawerH / 2 + 0.006, -drawerDepth / 2 + 0.008]}>
                      <boxGeometry args={[usableW - 0.02, 0.006, drawerDepth]} />
                      <meshStandardMaterial color={carcassColor} />
                    </mesh>
                    {/* Paroi latérale gauche (hauteur fixe si casserolier) */}
                    <mesh position={[-usableW / 2 + 0.008, sideOffset, -drawerDepth / 2 + 0.008]}>
                      <boxGeometry args={[0.005, sideH - 0.012, drawerDepth]} />
                      <meshStandardMaterial color={carcassColor} />
                    </mesh>
                    {/* Paroi latérale droite */}
                    <mesh position={[usableW / 2 - 0.008, sideOffset, -drawerDepth / 2 + 0.008]}>
                      <boxGeometry args={[0.005, sideH - 0.012, drawerDepth]} />
                      <meshStandardMaterial color={carcassColor} />
                    </mesh>
                    {/* Paroi arrière (hauteur des côtés) */}
                    <mesh position={[0, sideOffset, -drawerDepth + 0.008]}>
                      <boxGeometry args={[usableW - 0.02, sideH - 0.012, 0.005]} />
                      <meshStandardMaterial color={carcassColor} />
                    </mesh>
                    {/* Tubes de rehausse (casserolier uniquement) */}
                    {isTall && (() => {
                      const tubeR = 0.003;
                      const tubeLen = drawerDepth - 0.02;
                      const tubeY = sideOffset + sideH / 2 + (drawerH / 2 - (sideOffset + sideH / 2)) / 2;
                      return (
                        <>
                          <mesh position={[-usableW / 2 + 0.012, tubeY, -tubeLen / 2 + 0.005]} rotation={[Math.PI / 2, 0, 0]}>
                            <cylinderGeometry args={[tubeR, tubeR, tubeLen, 10]} />
                            <meshStandardMaterial {...tubeMat} />
                          </mesh>
                          <mesh position={[usableW / 2 - 0.012, tubeY, -tubeLen / 2 + 0.005]} rotation={[Math.PI / 2, 0, 0]}>
                            <cylinderGeometry args={[tubeR, tubeR, tubeLen, 10]} />
                            <meshStandardMaterial {...tubeMat} />
                          </mesh>
                        </>
                      );
                    })()}
                  </InteractiveDrawer>
                );
              }
              return null;
            })}
          </group>
        );
      })}

      {/* Salle de Bain : rendu du dessus selon sdb_top_finish */}
      {options?.sdb_top_finish === "avec_dessus" && templateVariant === "sdb" && (() => {
        const caissonHeight = h;
        const topPlateThickness = 0.02;
        return (
          <mesh position={[0, (caissonHeight / 2) + (topPlateThickness / 2), 0]}>
            <boxGeometry args={[w_actual + 0.03, topPlateThickness, d + 0.02]} />
            <meshStandardMaterial color={carcassColor} />
          </mesh>
        );
      })()}

      {/* Salle de Bain : N façades tiroirs empilées (sdb-1tiroir / 2tiroirs / 3tiroirs).
          Chaque tiroir a une façade + un caisson intérieur (parois + fond) — volume réaliste. */}
      {templateVariant === "sdb" && sdbDrawers > 0 && (() => {
        const usableH = h - 0.02;
        const bandH = usableH / sdbDrawers;
        const gap = 0.003;
        const faceThick = 0.018;
        const drawerDepth = d - 0.03;
        return (
          <group position={[0, -h / 2 + 0.01, 0]} name={`sdb-drawers-${sdbDrawers}`}>
            {Array.from({ length: sdbDrawers }).map((_, i) => {
              const cy = i * bandH + bandH / 2;
              const drH = bandH - gap;
              const closedZ = d / 2 - faceThick / 2 + 0.002;
              const openZ = closedZ + drawerDepth * 0.8;
              return (
                <InteractiveDrawer key={`sdb-face-${i}`} cx={0} cy={cy} closedZ={closedZ} openZ={openZ}>
                  {/* Façade tiroir SdB — offset +2mm pour éviter le Z-fighting avec les côtés/dessus du caisson */}
                  <mesh castShadow receiveShadow>
                    <boxGeometry args={[w_actual - 0.006, drH, faceThick]} />
                    <meshStandardMaterial {...sdbDrawerMatProps} />
                  </mesh>
                  {/* Poignée intégrée en haut de la façade — même catalogue que les portes (recto-verso/profil alu/stripe), sans poignée si TIP-ON */}
                  <Handle3D svgType={handleSvgType} centerX={0} centerY={drH / 2 - 0.012} z={faceThick / 2 + 0.004} availW={w_actual - 0.03} />
                  {/* Caisson intérieur du tiroir (fond + parois latérales + arrière) — toujours
                      blanc (intérieur standard), indépendant de la finition côté/façade. */}
                  <mesh position={[0, -drH / 2 + 0.006, -drawerDepth / 2 - faceThick / 2 + 0.008]}>
                    <boxGeometry args={[w_actual - 0.03, 0.006, drawerDepth]} />
                    <meshStandardMaterial color="#F7F7F3" />
                  </mesh>
                  <mesh position={[-(w_actual / 2) + 0.012, 0, -drawerDepth / 2 - faceThick / 2 + 0.008]}>
                    <boxGeometry args={[0.005, drH - 0.02, drawerDepth]} />
                    <meshStandardMaterial color="#F7F7F3" />
                  </mesh>
                  <mesh position={[(w_actual / 2) - 0.012, 0, -drawerDepth / 2 - faceThick / 2 + 0.008]}>
                    <boxGeometry args={[0.005, drH - 0.02, drawerDepth]} />
                    <meshStandardMaterial color="#F7F7F3" />
                  </mesh>
                  <mesh position={[0, 0, -drawerDepth - faceThick / 2 + 0.008]}>
                    <boxGeometry args={[w_actual - 0.03, drH - 0.02, 0.005]} />
                    <meshStandardMaterial color="#F7F7F3" />
                  </mesh>
                </InteractiveDrawer>
              );
            })}
          </group>
        );
      })()}
      {options?.sdb_top_finish === "avec_vasque" && templateVariant === "sdb" && (() => {
        const caissonHeight = h;
        const topPlateThickness = 0.025;
        // La vasque est ENCASTRÉE dans le plateau (moulage résine intégré) : le bord/rebord
        // est au niveau de la surface du plan, et la cuve descend SOUS le plan (pas posée dessus).
        const vasqueY = topPlateThickness / 2;
        // Dimensions réelles produit : vasque 400×280mm, profondeur de cuve 120mm — avec garde-fou
        // minimal si le caisson est exceptionnellement étroit/peu profond.
        const vasqueH = 0.12;
        const wallT = 0.015;
        const resinWhite = "#F7F7F3"; // même blanc pour le plan résine et la vasque (moulage intégré)
        const doubleVasque = !!options?.sdb_double_vasque;
        const vasqueD = Math.min(0.30, d - 0.04);
        const baseHalfW = (w_actual + 0.03) / 2; // moitié de la largeur de base du plan (hors débord serviette)
        // Porte-serviette : trou allongé le long de la PROFONDEUR (comme sur la photo produit),
        // percé dans le DÉBORD du plan (le plan continue au-delà du caisson sur ce côté — zone
        // "flottante" sans caisson dessous, comme sur la photo), avec 5cm de marge conservée à
        // l'avant et à l'arrière pour ne jamais toucher la crédence.
        const hasTowelSlot = (options?.sdb_porte_serviette_qty || 0) > 0;
        const towelSide = options?.sdb_porte_serviette_side || "droite";
        const towelSidesActive = hasTowelSlot ? (towelSide === "both" ? ["gauche", "droite"] : [towelSide]) : [];
        const towelOverhang = hasTowelSlot ? 0.09 : 0;
        const plateLeftX = -baseHalfW - (towelSidesActive.includes("gauche") ? towelOverhang : 0);
        const plateRightX = baseHalfW + (towelSidesActive.includes("droite") ? towelOverhang : 0);
        const plateD = d + 0.02;
        const frontStripD = (plateD / 2) - (vasqueD / 2);
        const minMargin = 0.03;

        // 1 ou 2 vasques selon l'option (double vasque dispo ≥1200 mm, validé côté serveur).
        let vasqueGroups;
        if (doubleVasque) {
          const vasqueW2 = Math.min(0.32, (w_actual / 2) - 0.08);
          const quarterOffset = baseHalfW / 2;
          vasqueGroups = [
            { cx: -quarterOffset, w: vasqueW2 },
            { cx: quarterOffset, w: vasqueW2 },
          ];
        } else {
          const vasquePos = options?.sdb_vasque_position || "centre";
          const vasqueW1 = Math.min(0.40, w_actual - 0.06);
          const maxOffset = Math.max(0, baseHalfW - (vasqueW1 / 2) - minMargin);
          const cx = vasquePos === "gauche" ? -maxOffset : vasquePos === "droite" ? maxOffset : 0;
          vasqueGroups = [{ cx, w: vasqueW1 }];
        }

        // Bandes de plateau pleines (résine) comblant l'espace entre les ouvertures de vasque(s)
        // et les bords du plan — généralisé à N ouvertures (1 ou 2).
        const sorted = [...vasqueGroups].sort((a, b) => a.cx - b.cx);
        const seq = [plateLeftX];
        sorted.forEach((o) => { seq.push(o.cx - o.w / 2); seq.push(o.cx + o.w / 2); });
        seq.push(plateRightX);
        const fillerStrips = [];
        for (let i = 0; i < seq.length; i += 2) {
          const segW = seq[i + 1] - seq[i];
          if (segW > 0.002) fillerStrips.push({ offsetX: (seq[i] + seq[i + 1]) / 2, w: segW });
        }
        const plateMatProps = { color: resinWhite, roughness: 0.3, metalness: 0.05 };

        return (
          <group position={[0, (caissonHeight / 2) + (topPlateThickness / 2), 0]}>
            {/* Bandes pleine profondeur comblant l'espace HORS ouverture(s) de vasque — plan
                visuellement continu partout ailleurs, y compris sur le débord (pas de découpe
                artificielle avant/arrière qui créerait une marche là où il n'y a pas de cuve). */}
            {fillerStrips.map((s, i) => (
              <mesh key={`fill-${i}`} position={[s.offsetX, 0, 0]}>
                <boxGeometry args={[s.w, topPlateThickness, plateD]} />
                <meshStandardMaterial {...plateMatProps} />
              </mesh>
            ))}
            {/* Pour chaque vasque : bandes avant/arrière LOCALES (juste à la largeur de son
                ouverture) pour créer l'ouverture rectangulaire sous la cuve. */}
            {vasqueGroups.map((vg, vi) => (
              <group key={`cap-${vi}`}>
                <mesh position={[vg.cx, 0, (vasqueD / 2) + (frontStripD / 2)]}>
                  <boxGeometry args={[vg.w, topPlateThickness, frontStripD]} />
                  <meshStandardMaterial {...plateMatProps} />
                </mesh>
                <mesh position={[vg.cx, 0, -(vasqueD / 2) - (frontStripD / 2)]}>
                  <boxGeometry args={[vg.w, topPlateThickness, frontStripD]} />
                  <meshStandardMaterial {...plateMatProps} />
                </mesh>
              </group>
            ))}

            {/* Vasque(s) résine encastrée(s) (cuve moulée, descend sous le rebord du plan) */}
            {vasqueGroups.map((vg, vi) => (
              <group key={vi} position={[vg.cx, vasqueY, 0]}>
                <mesh position={[0, -vasqueH + wallT / 2, 0]}>
                  <boxGeometry args={[vg.w, wallT, vasqueD]} />
                  <meshStandardMaterial color={resinWhite} emissive={resinWhite} emissiveIntensity={0.25} roughness={0.25} metalness={0.05} />
                </mesh>
                <mesh position={[-vg.w / 2 + wallT / 2, -vasqueH / 2, 0]}>
                  <boxGeometry args={[wallT, vasqueH, vasqueD]} />
                  <meshStandardMaterial color={resinWhite} emissive={resinWhite} emissiveIntensity={0.2} roughness={0.25} metalness={0.05} />
                </mesh>
                <mesh position={[vg.w / 2 - wallT / 2, -vasqueH / 2, 0]}>
                  <boxGeometry args={[wallT, vasqueH, vasqueD]} />
                  <meshStandardMaterial color={resinWhite} emissive={resinWhite} emissiveIntensity={0.2} roughness={0.25} metalness={0.05} />
                </mesh>
                <mesh position={[0, -vasqueH / 2, vasqueD / 2 - wallT / 2]}>
                  <boxGeometry args={[vg.w - wallT * 2, vasqueH, wallT]} />
                  <meshStandardMaterial color={resinWhite} emissive={resinWhite} emissiveIntensity={0.2} roughness={0.25} metalness={0.05} />
                </mesh>
                <mesh position={[0, -vasqueH / 2, -vasqueD / 2 + wallT / 2]}>
                  <boxGeometry args={[vg.w - wallT * 2, vasqueH, wallT]} />
                  <meshStandardMaterial color={resinWhite} emissive={resinWhite} emissiveIntensity={0.2} roughness={0.25} metalness={0.05} />
                </mesh>
                <mesh position={[0, -vasqueH + wallT + 0.001, 0]}>
                  <cylinderGeometry args={[0.025, 0.025, 0.002, 16]} />
                  <meshStandardMaterial color="#222" roughness={0.2} metalness={0.8} />
                </mesh>
                {/* Trou du robinet : simple indicateur (pas de mitigeur modélisé) */}
                <mesh position={[0, -vasqueY, -(vasqueD / 2) - 0.028]}>
                  <cylinderGeometry args={[0.011, 0.011, topPlateThickness + 0.006, 20]} />
                  <meshStandardMaterial color="#0A0A0A" roughness={0.9} metalness={0} />
                </mesh>
              </group>
            ))}

            {/* Porte-serviette intégré : trou allongé le long de la PROFONDEUR (comme sur la
                photo produit), percé dans le DÉBORD du plan (zone flottante au-delà du
                caisson), avec 5cm de marge conservée à l'avant ET à l'arrière — côté(s)
                choisi(s) librement, gauche, droite, ou les 2 ensemble. */}
            {towelSidesActive.map((side) => {
              const marginZ = 0.05; // 5 cm avant et arrière
              const slotLenZ = Math.max(0.05, plateD - 2 * marginZ);
              const slotWidthX = 0.03;
              const slotCenterX = side === "gauche" ? plateLeftX + (towelOverhang / 2) : plateRightX - (towelOverhang / 2);
              return (
                <mesh key={side} position={[slotCenterX, 0, 0]}>
                  <boxGeometry args={[slotWidthX, topPlateThickness + 0.006, slotLenZ]} />
                  <meshStandardMaterial color="#1A1A1A" roughness={0.4} metalness={0} transparent opacity={0.3} />
                </mesh>
              );
            })}
          </group>
        );
      })()}

      {/* Crédence SdB (jusqu'à 3 côtés : fond, gauche, droite) */}
      {(() => {
        const credenceSides = Array.from(new Set([
          ...((options?.sdb_credence_sides && options.sdb_credence_sides.length) ? options.sdb_credence_sides : ((options?.sdb_credence_ml || 0) > 0 ? ["fond"] : [])),
          ...((options?.sdb_credence_soudee_sides && options.sdb_credence_soudee_sides.length) ? options.sdb_credence_soudee_sides : ((options?.sdb_credence_soudee_ml || 0) > 0 ? ["fond"] : [])),
        ]));
        if (templateVariant !== "sdb" || credenceSides.length === 0) return null;
        const caissonHeight = h;
        let worktopTopY = (caissonHeight / 2);
        if (options?.sdb_top_finish === "avec_vasque") worktopTopY = (caissonHeight / 2) + 0.025; // on top of the plate
        else if (options?.sdb_top_finish === "avec_dessus") worktopTopY = (caissonHeight / 2) + 0.02; // on top of the plate
        else worktopTopY = (caissonHeight / 2) + 0.01;

        const baseHalfW = (w_actual + 0.03) / 2;
        const credenceHeight = 0.10; // 100mm
        const credenceMat = { color: "#F7F7F3", roughness: 0.3, metalness: 0.05 };
        // La crédence doit suivre le bord du plan : si le porte-serviette crée un débord sur un
        // côté (plan "avec vasque"), la crédence (fond + côté concerné) s'étend jusqu'à ce bord.
        const hasTowelSlot = (options?.sdb_porte_serviette_qty || 0) > 0 && options?.sdb_top_finish === "avec_vasque";
        const towelSide = options?.sdb_porte_serviette_side || "droite";
        const towelSidesActive = hasTowelSlot ? (towelSide === "both" ? ["gauche", "droite"] : [towelSide]) : [];
        const towelOverhang = hasTowelSlot ? 0.09 : 0;
        const credLeftX = -baseHalfW - (towelSidesActive.includes("gauche") ? towelOverhang : 0);
        const credRightX = baseHalfW + (towelSidesActive.includes("droite") ? towelOverhang : 0);
        return (
          <group>
            {credenceSides.includes("fond") && (
              <mesh position={[(credLeftX + credRightX) / 2, worktopTopY + (credenceHeight / 2), -d / 2 - 0.01 + 0.0075]}>
                <boxGeometry args={[credRightX - credLeftX, credenceHeight, 0.015]} />
                <meshStandardMaterial {...credenceMat} />
              </mesh>
            )}
            {credenceSides.includes("gauche") && (
              <mesh position={[credLeftX + 0.0025, worktopTopY + (credenceHeight / 2), 0]}>
                <boxGeometry args={[0.015, credenceHeight, d + 0.02]} />
                <meshStandardMaterial {...credenceMat} />
              </mesh>
            )}
            {credenceSides.includes("droite") && (
              <mesh position={[credRightX - 0.0025, worktopTopY + (credenceHeight / 2), 0]}>
                <boxGeometry args={[0.015, credenceHeight, d + 0.02]} />
                <meshStandardMaterial {...credenceMat} />
              </mesh>
            )}
          </group>
        );
      })()}

      {/* Plinthe (bandeau intégré sous le caisson) — sinon pieds visibles */}
      {hasPlinth ? (
        <mesh position={[0, -h / 2 - plinthH / 2, 0]}>
          <boxGeometry args={[w_actual, plinthH, d * 0.9]} />
          <meshStandardMaterial color={carcassColor} />
        </mesh>
      ) : (templateVariant === "sdb" || templateVariant === "sdb_column") ? (
        sdbFootH > 0 ? (
        // 4 pieds elliptiques
        [[-w_actual / 2 + 0.05, -h / 2 - sdbFootH / 2, -d / 2 + 0.05],
         [w_actual / 2 - 0.05, -h / 2 - sdbFootH / 2, -d / 2 + 0.05],
         [-w_actual / 2 + 0.05, -h / 2 - sdbFootH / 2, d / 2 - 0.05],
         [w_actual / 2 - 0.05, -h / 2 - sdbFootH / 2, d / 2 - 0.05]].map((pos, i) => (
          <mesh key={`sdb-foot-${i}`} position={pos}>
            <cylinderGeometry args={[0.02, 0.015, sdbFootH, 32]} />
            <meshStandardMaterial color="#222" metalness={0.8} roughness={0.2} />
          </mesh>
        ))
      ) : null
      ) : feetVisible ? (
        // 4 pieds proportionnels 100 mm (footH = 0.10 = 100 mm dans unité three)
        [[-w_actual / 2 + 0.02, -h / 2 - footH / 2, -d / 2 + 0.02],
         [w_actual / 2 - 0.02, -h / 2 - footH / 2, -d / 2 + 0.02],
         [-w_actual / 2 + 0.02, -h / 2 - footH / 2, d / 2 - 0.02],
         [w_actual / 2 - 0.02, -h / 2 - footH / 2, d / 2 - 0.02]].map((pos, i) => (
          <mesh key={`foot-${i}`} position={pos}>
            <boxGeometry args={[0.025, footH, 0.025]} />
            <meshStandardMaterial color="#222" metalness={0.55} roughness={0.4} />
          </mesh>
        ))
      ) : null}
    </group>
  );
}

export default function CabinetVisualizer3D({
  totalWidth = 120,
  height = 200,
  depth = 45,
  sections = [],
  carcassColor = "#B8935A",
  doorsColor = null,
  doorsDifferent = false,
  showDoors = true,
  templateVariant = "battant",
  options = null,
  sdbDrawers = 0,
  doorsFinishId = null,
  handleSvgType = null,
  domId = null, // identifiant DOM optionnel (ex: capture canvas->PNG pour le PDF, cf. QuoteDetail.jsx)
  printMode = false, // désactive les surlignages propres à l'éditeur (ex: tablette verrouillée en bleu) pour un rendu client (PDF)
}) {
  // Résolution des largeurs de section (asymétriques ou égales)
  const sectionWidths = useMemo(() => {
    const count = Math.max(1, sections.length);
    const explicit = sections.map((s) => s.width_cm || 0);
    if (explicit.every((w) => w > 0)) return explicit;
    return Array(count).fill(totalWidth / count);
  }, [sections, totalWidth]);

  // Cible caméra dynamique : centrer sur la moitié de la hauteur RÉELLE du meuble
  // (crucial pour les meubles SdB de 30/60/80 cm — sinon caméra pointe dans le vide).
  const cameraTarget = useMemo(() => {
    const h_m = height * 0.01;
    const groundOffset = (options?.plinth !== false ? 0.10 : 0.10); // ~10 cm plinthe/pieds
    return [0, h_m / 2 + groundOffset, 0];
  }, [height, options?.plinth]);

  // Position caméra proportionnelle à la taille du meuble (distance x2 en diagonale
  // en mode interactif Configurateur ; resserrée en printMode pour que le meuble
  // remplisse mieux le cadre fixe capturé dans le PDF — moins de recul, moins de
  // sol visible autour, sans risquer de rogner le haut du meuble).
  const cameraPos = useMemo(() => {
    const maxDim = Math.max(totalWidth, height, depth) * 0.01;
    const distMultiplier = printMode ? 1.7 : 2.2;
    const minDist = printMode ? 1.1 : 1.4;
    const dist = Math.max(minDist, maxDim * distMultiplier);
    return [dist * 0.55, dist * 0.55 + (height * 0.01) / 3, dist];
  }, [totalWidth, height, depth, printMode]);

  // Sol : disque discret qui reçoit les ombres, dimensionné sur l'emprise réelle du
  // meuble (plus grand sol fixe 4×4m en mode interactif, pour laisser de la marge à
  // l'orbite caméra ; sol resserré en printMode pour ne pas noyer le visuel du PDF).
  const floorSize = useMemo(() => {
    if (!printMode) return 4;
    const footprint = Math.max(totalWidth, depth) * 0.01;
    return Math.max(1.6, footprint * 2.0);
  }, [totalWidth, depth, printMode]);

  return (
    <div
      id={domId || undefined}
      data-testid="cabinet-visualizer-3d"
      className="w-full h-full"
      style={{ touchAction: "none" }}
    >
      <Canvas
        shadows
        camera={{ position: cameraPos, fov: printMode ? 38 : 45 }}
        style={{ background: "#F5F5F5" }}
        gl={{ preserveDrawingBuffer: true, antialias: true }}
      >
        {/* Éclairage studio pour meilleur contraste et volume */}
        <ambientLight intensity={0.45} />
        <directionalLight
          position={[4, 6, 4]}
          intensity={1.05}
          castShadow
          shadow-mapSize-width={1024}
          shadow-mapSize-height={1024}
          shadow-camera-left={-3}
          shadow-camera-right={3}
          shadow-camera-top={3}
          shadow-camera-bottom={-3}
          shadow-camera-near={0.1}
          shadow-camera-far={20}
        />
        <directionalLight position={[-4, 3, -3]} intensity={0.35} />
        <directionalLight position={[0, 2, -5]} intensity={0.2} />
        <Suspense fallback={null}>
          <CabinetMesh
            totalWidth={totalWidth}
            height={height}
            depth={depth}
            sections={sections}
            sectionWidths={sectionWidths}
            carcassColor={carcassColor}
            doorsColor={doorsColor}
            doorsDifferent={doorsDifferent}
            showDoors={showDoors}
            templateVariant={templateVariant}
            options={options}
            sdbDrawers={sdbDrawers}
            doorsFinishId={doorsFinishId}
            handleSvgType={handleSvgType}
            printMode={printMode}
          />
          {/* Sol : petit disque discret qui reçoit les ombres. Taille dynamique (cf.
              floorSize) pour ne pas noyer le meuble sur les petits gabarits SdB ni
              dans le visuel resserré du PDF (printMode). */}
          <mesh position={[0, 0, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
            <planeGeometry args={[floorSize, floorSize]} />
            <meshStandardMaterial color="#EAEAEA" roughness={0.85} />
          </mesh>
        </Suspense>
        {/* Caméra libérée : rotation ~360° verticale (au-dessus + presque en-dessous),
            target dynamique au centre du meuble (crucial pour les meubles SdB courts).
            Toujours montées (même en printMode) : c'est leur update() qui oriente la
            caméra vers cameraTarget — sans elles la caméra regarderait droit devant elle
            au lieu du meuble. */}
        <OrbitControls
          target={cameraTarget}
          enablePan={false}
          enableZoom={!printMode}
          enableRotate={!printMode}
          minDistance={0.6}
          maxDistance={8}
          minPolarAngle={0.05}
          maxPolarAngle={Math.PI - 0.05}
        />
      </Canvas>
    </div>
  );
}


export { CabinetMesh };
